import { CoreApplicationContext } from '@eventbrite/context-gen';
import { ConsumerHeaderSearchBar } from '@eventbrite/global-nav';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { getLogoInfo, getUserMenuInfo } from '@eventbrite/site-header';
import React from 'react';
import { FeatureFlags, RequestShape, UserShape } from '../../types/index';
import './ConsumerHeaderIndex.scss';
import {
    ConsumerHeaderDesktopLinks,
    ConsumerHeaderMobileLinks,
} from './ConsumerHeaderLinks';
import GlobalHeaderLogo from './GlobalHeaderLogo';
import { GlobalHeaderMenus } from './GlobalHeaderNavigationBar';

interface ConsumerHeaderProps {
    env: CoreApplicationContext['env'] & {
        launchNewSignInSignUpLinks?: string;
        isManageOverviewActive?: boolean;
    };
    request: RequestShape;
    user: UserShape;
    onAdditionalMenuTextItemSelect?: Function;
    isHomePage?: boolean;
    isSearchPage?: boolean;
    id?: string;
    featureFlags: FeatureFlags;
    hasBreadcrumbs?: boolean;
    searchQuery?: string;
    currentPlace?: string;
    isUsingCurrentLocation?: boolean;
    searchBar?: {
        renderSearchBar?: boolean;
        desktopLabel?: GenericLazyString | string;
        mobileLabel?: GenericLazyString | string;
        location?: GenericLazyString | string;
        isUsingCurrentLocation?: boolean;
    };
}

const ConsumerHeader: React.FunctionComponent<ConsumerHeaderProps> = (
    props,
) => {
    const {
        searchQuery = '',
        user,
        env,
        id,
        featureFlags,
        hasBreadcrumbs,
        request,
        isUsingCurrentLocation = false,
    } = props;

    const label = searchQuery || gettext('Search events');
    const searchBar = {
        renderSearchBar: true,
        desktopLabel: label,
        mobileLabel: label,
        isUsingCurrentLocation: isUsingCurrentLocation || false,
    };

    const { serverUrl, adminServerUrl, logoutUrl } = env;
    const { path: requestPath } = request;

    const userMenuInfo = getUserMenuInfo({
        adminServerUrl,
        featureFlags,
        logoutUrl,
        requestPath,
        serverUrl,
        showBrowseEvents: true,
        user,
    });

    const getHeaderClasses = () => {
        let headerClasses = 'consumer-header';
        if (hasBreadcrumbs) {
            headerClasses += ' consumer-header__has-breadcrumbs';
        }
        return headerClasses;
    };

    return (
        <header
            id={id || 'global-header'}
            className={getHeaderClasses()}
            data-spec="consumer-header"
        >
            <a
                className="consumer-header__skip-links eds-is-hidden-accessible"
                href="#skip-heading"
            >
                {gettext('Skip Main Navigation')}
            </a>
            <div className="consumer-header__main" data-role="header">
                <GlobalHeaderLogo
                    info={getLogoInfo(serverUrl, requestPath, {
                        iconWidth: '110px',
                    })}
                />
                <ConsumerHeaderSearchBar
                    user={props.user}
                    env={props.env}
                    searchBar={searchBar}
                />
                <div
                    className="consumer-header__desktop"
                    data-spec="consumer-header-desktop"
                >
                    <ConsumerHeaderDesktopLinks
                        user={user}
                        env={env}
                        featureFlags={featureFlags}
                        requestPath={requestPath}
                        isHomePage={props.isHomePage}
                        onAdditionalMenuTextItemSelect={
                            props.onAdditionalMenuTextItemSelect
                        }
                    />
                    <GlobalHeaderMenus userMenuInfo={userMenuInfo} />
                </div>
                <div
                    className="consumer-header__mobile"
                    data-spec="consumer-header-mobile"
                >
                    <ConsumerHeaderMobileLinks
                        user={user}
                        env={env}
                        isHomePage={props.isHomePage}
                        featureFlags={featureFlags}
                        requestPath={requestPath}
                    />
                </div>
            </div>
            <span id="skip-heading" className="eds-is-hidden-accessible">
                {gettext('Page Content')}
            </span>
        </header>
    );
};

export default ConsumerHeader;
